// Thin wrappers over /v1/auth/* + /v1/me.
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

import { apiClient } from "./client";
import { getDeviceSignals } from "@/utils/fingerprint";
import type { Me } from "@/store/auth";

const audience = import.meta.env.VITE_PAYOUTS_AUDIENCE || "payouts";

interface AccessTokenResponse {
  access_token: string;
}

export async function requestOtp(email: string): Promise<void> {
  await apiClient.post("/v1/auth/otp/request", { email, audience });
}

export async function verifyOtp(
  email: string,
  code: string,
): Promise<AccessTokenResponse> {
  // Optional device fingerprint signals — {} when VITE_FPJS_PUBLIC_KEY is
  // unset or if collection fails. Never blocks/throws the login flow.
  const signals = await getDeviceSignals(email);
  const resp = await apiClient.post<AccessTokenResponse>(
    "/v1/auth/otp/verify",
    { email, audience, code, ...signals },
  );
  return resp.data;
}

export async function refresh(): Promise<AccessTokenResponse> {
  const signals = await getDeviceSignals();
  const resp = await apiClient.post<AccessTokenResponse>("/v1/auth/refresh", {
    ...signals,
  });
  return resp.data;
}

export async function logout(): Promise<void> {
  await apiClient.post("/v1/auth/logout");
}

export async function getMe(): Promise<Me> {
  const resp = await apiClient.get<Me>("/v1/me");
  return resp.data;
}

// ── WebAuthn / passkeys ──────────────────────────────────────────────────────
//
// Contract (auth-service, proxied by the payouts gateway /v1/auth/* → /auth/*):
//
//   login/begin (public)     POST body {audience} → {publicKey}, hdr X-Challenge-ID
//   login/finish (public)    POST body {challenge_id, credential} → {access_token} + refresh cookie
//   register/begin (bearer)  POST body {name?} → {publicKey}, hdr X-Challenge-ID
//   register/finish (bearer) POST hdrs X-Challenge-ID (+ X-Challenge-Name), body = raw attestation → 204
//   credentials (bearer)     GET / PATCH / DELETE /v1/auth/webauthn/credentials[/:id]
//
// The challenge id is echoed on the begin RESPONSE header (X-Challenge-ID) and
// must be sent back on finish. axios lowercases response header keys, so read
// resp.headers['x-challenge-id']. All bearer calls ride the apiClient
// interceptor, so a stale access token is refreshed + retried transparently.

const CHALLENGE_ID_HEADER = "x-challenge-id";

/**
 * Passkey — one registered WebAuthn credential from
 * GET /v1/auth/webauthn/credentials. `name`/`last_used_at` are null when the
 * credential was registered without a label / has never signed in.
 */
export interface Passkey {
  id: number;
  name: string | null;
  created_at: string;
  last_used_at: string | null;
}

/** go-webauthn begin envelope: the inner publicKey is the optionsJSON. */
interface BeginRegistrationEnvelope {
  publicKey: PublicKeyCredentialCreationOptionsJSON;
}
interface BeginLoginEnvelope {
  publicKey: PublicKeyCredentialRequestOptionsJSON;
}
interface ListCredentialsResponse {
  credentials: Passkey[];
}

function readChallengeId(headers: unknown): number {
  const h = headers as Record<string, unknown> | undefined;
  const raw = h?.[CHALLENGE_ID_HEADER];
  const id = Number(raw);
  if (!raw || Number.isNaN(id)) {
    throw new Error("passkey ceremony failed (missing challenge id)");
  }
  return id;
}

/** login/begin — fetch assertion options + the correlation challenge id. */
export async function webauthnLoginBegin(): Promise<{
  options: PublicKeyCredentialRequestOptionsJSON;
  challengeId: number;
}> {
  const resp = await apiClient.post<BeginLoginEnvelope>(
    "/v1/auth/webauthn/login/begin",
    { audience },
  );
  return {
    options: resp.data.publicKey,
    challengeId: readChallengeId(resp.headers),
  };
}

/** login/finish — exchange the assertion for a session (sets refresh cookie). */
export async function webauthnLoginFinish(
  challengeId: number,
  credential: AuthenticationResponseJSON,
): Promise<AccessTokenResponse> {
  const signals = await getDeviceSignals();
  const resp = await apiClient.post<AccessTokenResponse>(
    "/v1/auth/webauthn/login/finish",
    { challenge_id: challengeId, credential, ...signals },
  );
  return resp.data;
}

/** register/begin (bearer) — fetch creation options + the challenge id. */
export async function webauthnRegisterBegin(name?: string): Promise<{
  options: PublicKeyCredentialCreationOptionsJSON;
  challengeId: number;
}> {
  const trimmed = name?.trim();
  const resp = await apiClient.post<BeginRegistrationEnvelope>(
    "/v1/auth/webauthn/register/begin",
    trimmed ? { name: trimmed } : {},
  );
  return {
    options: resp.data.publicKey,
    challengeId: readChallengeId(resp.headers),
  };
}

/**
 * register/finish (bearer) — the challenge id and optional name go in REQUEST
 * headers; the raw attestation JSON is the body. Returns 204.
 */
export async function webauthnRegisterFinish(
  challengeId: number,
  name: string | undefined,
  attestation: RegistrationResponseJSON,
): Promise<void> {
  const trimmed = name?.trim();
  const headers: Record<string, string> = {
    "X-Challenge-ID": String(challengeId),
  };
  if (trimmed) headers["X-Challenge-Name"] = trimmed;
  await apiClient.post("/v1/auth/webauthn/register/finish", attestation, {
    headers,
  });
}

export async function listWebauthnCredentials(): Promise<Passkey[]> {
  const resp = await apiClient.get<ListCredentialsResponse>(
    "/v1/auth/webauthn/credentials",
  );
  return Array.isArray(resp.data?.credentials) ? resp.data.credentials : [];
}

export async function renameWebauthnCredential(
  id: number,
  name: string,
): Promise<void> {
  await apiClient.patch(`/v1/auth/webauthn/credentials/${id}`, {
    name: name.trim(),
  });
}

export async function deleteWebauthnCredential(id: number): Promise<void> {
  await apiClient.delete(`/v1/auth/webauthn/credentials/${id}`);
}
